import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    AsyncSelectInput,
    autoinject,
    bind,
    CronInput,
    errorRedirect,
    FormStore,
    inject,
    Input,
    Loader,
    ModalHandle,
    Nullable,
    SubTopBar,
    TasksStore,
    Title,
    upperCaseEachWord,
    uuidv4generate,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsArrowLeftShort } from '@react-icons/all-files/bs/BsArrowLeftShort';
import { RouteComponentProps } from 'react-router';
import { LinkContainer } from 'react-router-bootstrap';
import { Button, Form, FormGroup } from 'reactstrap';
import { defaultTableColumns } from '../Account/AccountStore';
import { DisplayListViewModel } from '../DisplayFilters/DisplayListViewModel';
import { DisplaySelectorCard } from '../DisplayFilters/DisplaySelectorCard';
import { DisplaysStore } from '../Displays/DisplaysStore';
import { CloudConfigsStore } from './CloudConfigsStore';
import { ConfigConfirmModal } from './ConfigConfirmModal';

export interface IEditConfigParams {
    guid: string;
}

export interface IEditConfigLocationState {
    displayFilter?: Mri.DisplayFilters.DisplayFilter;
    copy?: string;
}

export interface ICreateOrEditConfigProps
    extends RouteComponentProps<IEditConfigParams, {}, IEditConfigLocationState> {}

@autoinject
@observer
export class CreateOrEditConfig extends React.Component<ICreateOrEditConfigProps> {
    public constructor(props: ICreateOrEditConfigProps) {
        super(props);

        this.vm = new DisplayListViewModel({
            onChange: this.onDisplayFilterChange,
            columns: defaultTableColumns,
        });
    }

    @action
    public async componentDidMount() {
        this.loading = true;
        try {
            this.fillForm();

            if (this.viewState == 'edit' && this.vm.displayFilter && !this.vm.displayFilter.displays?.length) {
                this.simpleDisplaySelector = false;
            }

            await this.vm.loadMoreAsync();
            await this.vm.loadContractsAsync();
        } catch (e: any) {
            errorRedirect(e);
        } finally {
            this.loading = false;
        }
    }

    public render() {
        return (
            <div className="container-fluid pt-3">
                <Title>{this.viewState == 'edit' ? 'Edit' : 'Create'} Config</Title>

                <SubTopBar>
                    <LinkContainer exact to="/cloud-configs/configs">
                        <a className="btn btn-link">
                            <BsArrowLeftShort style={{ fontSize: 25 }} /> Back To Configs
                        </a>
                    </LinkContainer>
                </SubTopBar>

                <Loader
                    loading={this.loading && !this.configForm}
                    message=" Loading Config..."
                    showSpinner
                    className="w-100 text-center"
                />

                {this.configForm && (
                    <Form onSubmit={this.submitAsync}>
                        <div className="row justify-content-center">
                            <div className="col-12 col-md-6 col-lg-5 mb-3">
                                <div className="card">
                                    <div className="card-header bg-btn-primary">Config</div>
                                    <div className="card-body">
                                        {!!this.configForm.globalErrors.length && (
                                            <div className="alert-danger p-2">
                                                <ul>
                                                    {this.configForm.globalErrors.map((e: string, idx: number) => (
                                                        <li key={idx}>{e}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        <div>
                                            <FormGroup color={this.configForm.fields.name.state}>
                                                <label>
                                                    <strong>Name</strong>
                                                </label>

                                                <Input
                                                    type="text"
                                                    field={this.configForm.fields.name}
                                                    placeholder="Name"
                                                    required
                                                />
                                            </FormGroup>

                                            <FormGroup color={this.configForm.fields.comment.state}>
                                                <label>
                                                    <strong>Comments</strong>
                                                </label>

                                                <Input
                                                    type="text"
                                                    field={this.configForm.fields.comment}
                                                    placeholder="Comments"
                                                />
                                            </FormGroup>

                                            <CronInput
                                                label="Server Restart Schedule"
                                                field={this.configForm.fields.schedule}
                                            />

                                            <div className="mb-3">
                                                <label>
                                                    <strong>Displays</strong>

                                                    <a
                                                        className="btn btn-link py-0"
                                                        onClick={this.toggleDisplaySelector}
                                                    >
                                                        {!this.simpleDisplaySelector ? 'Simple' : 'Advanced'}
                                                    </a>
                                                </label>

                                                {!this.simpleDisplaySelector && (
                                                    <p>
                                                        <small>
                                                            *Note: All Displays shown under the Display Filter will be
                                                            added to this Config*
                                                        </small>
                                                    </p>
                                                )}

                                                {this.simpleDisplaySelector && (
                                                    <AsyncSelectInput
                                                        className="w-100 react-select-container d-inline-block"
                                                        placeholder="Serial Number"
                                                        loadOptions={this.loadOptionsAsync}
                                                        getOptionLabel={x => x!}
                                                        getOptionValue={x => x!}
                                                        defaultOptions
                                                        isMulti
                                                        field={this.configForm.fields.displayFilter.fields.displays}
                                                    />
                                                )}
                                            </div>

                                            <FormGroup color={this.configForm.fields.configJson.state}>
                                                <label>
                                                    <strong>Config</strong>
                                                </label>

                                                <Input
                                                    type="textarea"
                                                    field={this.configForm.fields.configJson}
                                                    placeholder="Config"
                                                    required
                                                />
                                            </FormGroup>

                                            <FormGroup color={this.configForm.fields.active.state}>
                                                <label>
                                                    <strong>Active</strong>
                                                </label>

                                                <Input
                                                    type="checkbox"
                                                    field={this.configForm.fields.active}
                                                    placeholder="Active"
                                                    className="ms-3"
                                                />
                                            </FormGroup>

                                            <FormGroup>
                                                <Button
                                                    type="submit"
                                                    color="primary"
                                                    disabled={
                                                        this.simpleDisplaySelector &&
                                                        !this.configForm.fields.displayFilter.value.displays?.length
                                                    }
                                                >
                                                    <Loader
                                                        loading={this.loading}
                                                        showSpinner={true}
                                                        message="Applying..."
                                                    />
                                                    {!this.loading && (
                                                        <span>
                                                            {this.viewState == 'create' && 'Create Config'}
                                                            {this.viewState == 'edit' && 'Update'}
                                                        </span>
                                                    )}
                                                </Button>
                                            </FormGroup>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {!this.simpleDisplaySelector && (
                                <div className="col-12 col-md-6 col-lg-7">
                                    <DisplaySelectorCard history={this.props.history} vm={this.vm} />
                                </div>
                            )}

                            <ConfigConfirmModal vm={this.vm} modalHandle={this.modalHandle} />
                        </div>
                    </Form>
                )}
            </div>
        );
    }

    @computed
    private get viewState() {
        const parthParts = this.props.location.pathname.split('/');
        return parthParts[parthParts.length - 1];
    }

    @computed
    private get config() {
        return this.cloudConfigsStore.configs.get(this.props.match.params.guid ?? this.props.location.state?.copy);
    }

    @bind
    @action
    private toggleDisplaySelector() {
        this.vm.clearAll();
        this.simpleDisplaySelector = !this.simpleDisplaySelector;
    }

    @bind
    private async loadOptionsAsync(value: string) {
        const search: Mri.Web.Internal.Controllers.Displays.SearchModel = {
            displayFilter: {
                serialNumberSearch: value,
            },
            limit: 10,
            offset: 0,
            orderBy: '',
            searchGuid: uuidv4generate(),
            aggregates: false,
            config: false,
            status: false,
            software: false,
            telemetry: false,
            contract: false,
            where: '',
        };
        const response = await this.displaysStore.loadWhereSearchAsync(search);
        if (response) {
            return response.result;
        } else {
            return [];
        }
    }

    @action
    private fillForm() {
        if (this.viewState == 'edit' && !this.config) throw new Error('Config does not exist');

        if (this.config) {
            if (this.viewState == 'create') {
                this.configForm = new FormStore<Mri.CloudConfigs.CloudConfig>({
                    ...this.config,
                    name: '',
                    cloudConfigGuid: uuidv4generate(),
                    versionId: uuidv4generate(),
                });
            } else {
                this.configForm = new FormStore<Mri.CloudConfigs.CloudConfig>({
                    ...this.config,
                    versionId: uuidv4generate(),
                });
            }

            this.vm = new DisplayListViewModel({
                onChange: this.onDisplayFilterChange,
                displayFilter: this.configForm.values.displayFilter,
                columns: defaultTableColumns,
            });
        } else {
            const model: Nullable<Mri.CloudConfigs.CloudConfig> = {
                name: '',
                comment: '',
                schedule: '',
                active: false,
                cloudConfigGuid: uuidv4generate(),
                displayFilter: this.vm.displayFilter,
                configJson: '',
                versionId: uuidv4generate(),
            };

            if (this.props.location?.state?.displayFilter)
                Object.assign(model.displayFilter!, this.props.location.state.displayFilter); //TODO TEST UPGRADE

            this.configForm = new FormStore<Mri.CloudConfigs.CloudConfig>(model);
            this.vm = new DisplayListViewModel({
                onChange: this.onDisplayFilterChange,
                columns: defaultTableColumns,
            });
        }
    }

    @bind
    @action
    private async submitAsync(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (!this.configForm) throw new Error('Form does not exist');

        this.configForm.onSubmit();
        if (this.configForm.validate()) {
            this.loading = true;
            if (this.simpleDisplaySelector) {
                this.vm.displayFilter.displays = this.configForm.values.displayFilter.displays;
            }
            if (await this.modalHandle.confirm()) {
                const task = this.tasksStore.startSessionTask(upperCaseEachWord(this.viewState) + ' Config');

                try {
                    if (this.viewState == 'edit') {
                        await this.cloudConfigsStore.updateAsync(this.props.match.params.guid, this.configForm.values);
                    } else if (this.viewState == 'create') {
                        await this.cloudConfigsStore.createAsync(this.configForm.values);
                    }
                    task.complete();
                    this.props.history.push(`/cloud-configs/configs`);
                } catch (e: any) {
                    if (e.modelState) {
                        this.configForm.clearGlobalErrors();
                        this.configForm.addErrors(e.modelState);
                    }

                    task.error(e);
                } finally {
                    this.loading = false;
                }
            } else {
                this.loading = false;
            }
        } else {
            console.log('invalid');
            this.loading = false;
        }
    }

    @bind
    @action
    private onDisplayFilterChange(displayFilter: Mri.DisplayFilters.DisplayFilter) {
        if (!this.configForm) throw new Error('Form does not exist');

        this.configForm.fields.displayFilter.setValue(displayFilter);
    }

    private vm!: DisplayListViewModel;
    private modalHandle = new ModalHandle();
    @observable private configForm?: FormStore<Mri.CloudConfigs.CloudConfig>;
    @observable private simpleDisplaySelector = true;
    @observable private loading = false;

    @inject private readonly cloudConfigsStore!: CloudConfigsStore;
    @inject private readonly displaysStore!: DisplaysStore;
    @inject private tasksStore!: TasksStore;
}
